"use client";
import React, { useEffect, useMemo } from "react";
import useResourceStore from "~/stores/resource-store";
import { Button } from "~/components/ui/button";
import * as Accordion from "@radix-ui/react-accordion";
import { PlusIcon, MinusIcon } from "@radix-ui/react-icons";
import "~/styles/resources.css";

export const ResourceFilters = ({ topics, allResourcesData }) => {
  const topicsFilterOptions = useResourceStore(
    (state) => state.topicsFilterOptions,
  );
  const setTopicsFilterOptions = useResourceStore(
    (state) => state.setTopicsFilterOptions,
  );
  const typeFilterOptions = useResourceStore(
    (state) => state.typeFilterOptions,
  );
  const toggleFilterItem = useResourceStore((state) => state.toggleFilterItem);
  const activeFilters = useResourceStore((state) => state.filters);

  const extractTitles = (topics) => {
    return topics.map((topic) => topic.attributes.title);
  };

  useEffect(() => {
    const titles = extractTitles(topics);
    setTopicsFilterOptions(titles);
  }, [topics]);

  // Filter data based on active filters
  const filteredData = useMemo(() => {
    return allResourcesData.filter((resource) => {
      const matchesTopic =
        activeFilters.topic.length === 0 ||
        resource?.blog_topics?.data?.some((topicItem) =>
          activeFilters.topic.includes(topicItem?.attributes?.title),
        );

      const matchesType =
        activeFilters.type.length === 0 ||
        activeFilters.type.includes(resource?.fileType);

      const matchesSearchQuery =
        activeFilters.searchQuery.trim() === "" ||
        resource.title
          .toLowerCase()
          .includes(activeFilters.searchQuery.trim().toLowerCase()) ||
        resource.blog_topics.data.some((topic) =>
          topic.attributes.title
            .toLowerCase()
            .includes(activeFilters.searchQuery.trim().toLowerCase()),
        ) ||
        resource.blog_category?.data?.attributes?.title
          ?.toLowerCase()
          ?.includes(activeFilters.searchQuery.trim().toLowerCase());
      return matchesTopic && matchesType && matchesSearchQuery;
    });
  }, [allResourcesData, activeFilters]);

  // Memoized counts for topics based on filtered data
  const topicCounts = useMemo(() => {
    const counts = {};
    topicsFilterOptions.forEach((topic) => {
      counts[topic] = filteredData.filter((resource) =>
        resource?.blog_topics?.data?.some(
          (topicItem) => topicItem?.attributes?.title === topic,
        ),
      ).length;
    });
    return counts;
  }, [topicsFilterOptions, filteredData]);

  // Memoized counts for types based on filtered data
  const typeCounts = useMemo(() => {
    const counts = {};
    typeFilterOptions.forEach((type) => {
      counts[type] = filteredData.filter(
        (resource) => resource?.fileType === type,
      ).length;
    });
    return counts;
  }, [typeFilterOptions, filteredData]);

  return (
    <div className="col-span-1 text-dark-gray">
      <div className="flex items-end justify-between">
        <h5 className="font-h5">Filter By</h5>
        <Button
          className={`h-fit self-end rounded-sm p-1 hover:bg-[#ffffff7A] ${
            activeFilters.topic.length > 0 || activeFilters.type.length > 0
              ? "font-medium"
              : ""
          }`}
          variant="ghost"
          onClick={() => {
            useResourceStore.getState().resetFilters();
            const url = new URL(window?.location?.href);
            url.search = "";
            window?.history?.replaceState(null, "", url.toString());
            window?.location?.reload();
          }}
        >
          clear all
        </Button>
      </div>
      <div className="py-3">
        <Accordion.Root
          type="multiple"
          defaultValue={["topics", "types"]}
          className=""
        >
          <Accordion.Item value="topics">
            <Accordion.Header className="flex justify-between border-b border-b-slate-700 py-3">
              <Accordion.Trigger className="flex w-full items-center justify-between gap-3 pr-2 transition-transform duration-200 hover:text-black">
                <h6 className="font-h6 uppercase">TOPICS</h6>
                <PlusIcon className="plus-icon h-4 w-4 shrink-0" />
                <MinusIcon className="minus-icon h-4 w-4 shrink-0" />
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className="flex flex-col gap-2 py-3">
              {topicsFilterOptions.sort().map((topic, i) => (
                <Button
                  className={`flex w-full justify-between px-2 ${
                    activeFilters.topic.includes(topic)
                      ? "bg-[#4143441A]"
                      : "hover:bg-[#ffffff7A]"
                  }`}
                  variant="ghost"
                  key={i}
                  onClick={() => toggleFilterItem("topic", topic)}
                >
                  <p className="font-p">{topic}</p>
                  <p>{topicCounts[topic] || 0}</p>
                </Button>
              ))}
            </Accordion.Content>
          </Accordion.Item>
          <Accordion.Item value="types">
            <Accordion.Header className="flex justify-between border-b border-b-slate-700 py-3">
              <Accordion.Trigger className="flex w-full items-center justify-between gap-3 pr-2 transition-transform duration-200 hover:text-black">
                <h6 className="font-h6 uppercase">Types</h6>
                <PlusIcon className="plus-icon h-4 w-4 shrink-0" />
                <MinusIcon className="minus-icon h-4 w-4 shrink-0" />
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className="flex flex-col gap-2 py-3">
              {typeFilterOptions.map((type, i) => (
                <Button
                  className={`flex w-full justify-between px-2 ${
                    activeFilters.type.includes(type)
                      ? "bg-[#4143441A]"
                      : "hover:bg-[#ffffff7A]"
                  }`}
                  variant="ghost"
                  key={`${i}-types`}
                  onClick={() => toggleFilterItem("type", type)}
                >
                  <p className="font-p">{type}</p>
                  <p>{typeCounts[type] || 0}</p>
                </Button>
              ))}
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      </div>
    </div>
  );
};
